import type { CropSolution, DosageRow, Product } from '../types';
import { extractProductFacts, doseRowsForCrop, type ProductFacts } from './productFacts';
import { cropNames } from './cropLinks';
import type { FaqEntry } from './productFaq';

/**
 * A crop's programme, assembled from the products' OWN dose tables.
 *
 * This is the page an assistant needs to answer "what is the dose of AGN for grapes" and "which
 * GreenbayBio products are used for paddy". Until now a crop page carried an editorial note and some
 * product cards; the actual per-crop rows — application number, stage, dose — existed only inside each
 * product's dose table and were never brought together by crop.
 *
 * MATCHING IS BY WHOLE CROP NAME, and a row is reproduced verbatim. Where a product is recommended for
 * a crop but its sheet holds no row naming that crop, the entry says so rather than borrowing a dose
 * from a neighbouring row — a dose invented by interpolation is a damaged crop.
 */
export interface CropProductEntry {
  product: Product;
  facts: ProductFacts;
  /** Rows from this product's dose table that name this crop. Empty when the sheet has none. */
  rows: DosageRow[];
}

export interface CropProgramme {
  crop: CropSolution;
  entries: CropProductEntry[];
  /** Products recommended for the crop whose sheet carries no row naming it. */
  withoutCropRows: Product[];
  /** Distinct stage/timing wording across the matched rows, in the order the sheets list it. */
  stages: string[];
  /** De-duplicated application methods across the matched products (for one-line summaries). */
  applicationMethods: string[];
  /** Methods per product — what a reader needs, since a method belongs to a product, not to a crop. */
  methodsByProduct: { product: Product; methods: string[] }[];
  /** De-duplicated precaution lines across the matched products. */
  precautions: string[];
  /** Every technical document attached to the matched products. */
  documents: { product: Product; label: string; url: string; lang?: string }[];
}

export function buildCropProgramme(crop: CropSolution, products: Product[]): CropProgramme {
  const names = cropNames(crop);
  const entries: CropProductEntry[] = products.map((product) => {
    const facts = extractProductFacts(product);
    return { product, facts, rows: doseRowsForCrop(facts, names) };
  });

  const stages: string[] = [];
  for (const e of entries) {
    for (const r of e.rows) {
      const t = (r.timing ?? '').trim();
      if (t && !stages.includes(t)) stages.push(t);
    }
  }

  const applicationMethods: string[] = [];
  const methodsByProduct: CropProgramme['methodsByProduct'] = [];
  const precautions: string[] = [];
  const documents: CropProgramme['documents'] = [];
  for (const e of entries) {
    if (e.facts.applicationMethods.length) methodsByProduct.push({ product: e.product, methods: e.facts.applicationMethods });
    for (const m of e.facts.applicationMethods) if (!applicationMethods.includes(m)) applicationMethods.push(m);
    for (const p of e.facts.precautions) if (!precautions.includes(p)) precautions.push(p);
    for (const r of e.product.resources ?? []) {
      documents.push({ product: e.product, label: r.label, url: r.url, lang: r.lang });
    }
  }

  return {
    crop,
    entries,
    withoutCropRows: entries.filter((e) => !e.rows.length).map((e) => e.product),
    stages,
    applicationMethods,
    methodsByProduct,
    precautions,
    documents,
  };
}

/** One flat table of every documented application for this crop, product by product. */
export interface CropScheduleRow {
  productName: string;
  productSlug: string;
  application?: string;
  timing?: string;
  dose: string;
  note?: string;
}

export function cropScheduleRows(programme: CropProgramme): CropScheduleRow[] {
  const out: CropScheduleRow[] = [];
  for (const e of programme.entries) {
    for (const r of e.rows) {
      out.push({
        productName: e.product.name,
        productSlug: e.product.slug,
        application: r.application,
        timing: r.timing,
        dose: r.dose,
        note: r.note,
      });
    }
  }
  return out;
}

/**
 * The crop page's FAQ — every answer assembled from matched rows and product records, never written.
 * A question is omitted when the data behind it is absent.
 */
export function cropFaq(programme: CropProgramme): FaqEntry[] {
  const { crop, entries } = programme;
  const withRows = entries.filter((e) => e.rows.length);
  const out: FaqEntry[] = [];
  const add = (q: string, a: string) => {
    const t = a.replace(/\s+/g, ' ').trim();
    if (t) out.push({ q, a: t });
  };

  add(
    `Which GreenbayBio products are documented for ${crop.name.toLowerCase()}?`,
    entries.length ? `${entries.map((e) => e.product.name).join(', ')}.` : '',
  );

  for (const e of withRows) {
    add(
      `What is the dose of ${e.product.name} for ${crop.name.toLowerCase()}?`,
      e.rows
        .map((r) =>
          [r.dose, r.application ? `(${r.application})` : '', r.timing ? `— ${r.timing}` : '', r.note ? `[${r.note}]` : '']
            .filter(Boolean)
            .join(' '),
        )
        .join('; ') + '. Follow the rate printed on your pack label.',
    );
  }

  add(
    `How are GreenbayBio products applied on ${crop.name.toLowerCase()}?`,
    programme.methodsByProduct.length
      ? programme.methodsByProduct.map((m) => `${m.product.name}: ${m.methods.join(', ')}`).join('. ') + '.'
      : '',
  );

  if (crop.note) add(`What is the GreenbayBio programme for ${crop.name.toLowerCase()}?`, crop.note);

  return out;
}
